import React from 'react'
import { gql, useMutation, useQuery } from 'urql'
import { Alert, Box, Button, Card, Typography, useTheme } from '@mui/material'
import AppLink from '../util/AppLink'
import { brandColors, serif } from './brand'
import { ago } from './api'

// GoAlert's urql client suspends by default; these pages render their own loading states.
const noSuspense = { suspense: false }

const query = gql`
  query ErecordsAckAlert($id: Int!) {
    alert(id: $id) {
      id
      alertID
      status
      summary
      createdAt
      service {
        name
      }
    }
  }
`

const mutation = gql`
  mutation ErecordsAckFromLink($input: UpdateAlertsInput!) {
    updateAlerts(input: $input) {
      id
      status
    }
  }
`

// AckFromLink is the target of the "Acknowledge" button on Teams cards. It
// never acknowledges on a plain GET (link previews would trigger it); it asks.
export default function AckFromLink(props: {
  alertID: string
}): React.JSX.Element {
  const c = brandColors[useTheme().palette.mode]
  const id = parseInt(props.alertID, 10)
  const [q] = useQuery({
    context: noSuspense,
    query,
    variables: { id },
  })
  const [m, commit] = useMutation(mutation)
  const a = q.data?.alert

  return (
    <Box sx={{ maxWidth: 520, mx: 'auto', mt: { xs: 1, sm: 6 } }}>
      <Card
        elevation={0}
        sx={{ borderRadius: '16px', border: `1px solid ${c.line}`, p: 3 }}
      >
        <Typography
          component='h1'
          className='erecords-serif'
          sx={{ fontFamily: serif, fontSize: 24, mb: 1 }}
        >
          Acknowledge alert #{props.alertID}
        </Typography>
        {q.error && <Alert severity='error'>{q.error.message}</Alert>}
        {a && (
          <React.Fragment>
            <Typography sx={{ fontWeight: 600 }}>{a.summary}</Typography>
            <Typography sx={{ fontSize: 13, color: c.muted, mb: 2 }}>
              {a.service?.name} · open {ago(a.createdAt)}
            </Typography>
            {a.status === 'StatusUnacknowledged' ? (
              <Button
                variant='contained'
                disabled={m.fetching}
                onClick={() =>
                  commit({
                    input: { alertIDs: [id], newStatus: 'StatusAcknowledged' },
                  })
                }
              >
                Acknowledge, I’m on it
              </Button>
            ) : (
              <Alert
                severity={a.status === 'StatusClosed' ? 'success' : 'info'}
              >
                {a.status === 'StatusClosed'
                  ? 'This alert is already resolved.'
                  : 'Already acknowledged. No more calls go out for it.'}
              </Alert>
            )}
            {m.error && (
              <Alert severity='error' sx={{ mt: 2 }}>
                {m.error.message}
              </Alert>
            )}
          </React.Fragment>
        )}
        <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
          <Button component={AppLink} to={`/alerts/${props.alertID}`}>
            Open alert
          </Button>
          <Button component={AppLink} to='/overview'>
            On-call overview
          </Button>
        </Box>
      </Card>
    </Box>
  )
}
